import React, { useState, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Upload, FileText, ArrowRight, Check, AlertTriangle, X, ChevronDown } from "lucide-react";

const TARGET_FIELDS = [
  { key: "transaction_id", label: "Transaction ID", required: true },
  { key: "offer_id", label: "Offer ID", required: true },
  { key: "affiliate_id", label: "Affiliate ID", required: true },
  { key: "status", label: "Status", required: false },
  { key: "payout", label: "Payout", required: false },
  { key: "revenue", label: "Revenue", required: false },
  { key: "sale_amount", label: "Sale Amount", required: false },
  { key: "country_code", label: "Country Code", required: false },
  { key: "event_name", label: "Event Name", required: false },
  { key: "sub1", label: "Sub1", required: false },
  { key: "sub2", label: "Sub2", required: false },
  { key: "sub3", label: "Sub3", required: false },
  { key: "sub4", label: "Sub4", required: false },
  { key: "sub5", label: "Sub5", required: false },
  { key: "source_id", label: "Source ID", required: false },
  { key: "adv_event_id", label: "Adv Event ID", required: false },
  { key: "coupon_code", label: "Coupon Code", required: false },
  { key: "currency_id", label: "Currency", required: false },
  { key: "created_at", label: "Date/Time", required: false },
  { key: "device_type", label: "Device Type", required: false },
  { key: "os", label: "OS", required: false },
  { key: "browser", label: "Browser", required: false },
];

type Step = "upload" | "map" | "importing" | "results";

interface ImportResult {
  success: boolean;
  totalRows: number;
  imported: number;
  errors: number;
  errorDetails: { row: number; message: string }[];
}

function autoMap(csvHeaders: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  const normalized = csvHeaders.map((h) => h.toLowerCase().replace(/[\s\-]+/g, "_"));

  for (const field of TARGET_FIELDS) {
    const exactIdx = normalized.indexOf(field.key);
    if (exactIdx >= 0) { map[field.key] = csvHeaders[exactIdx]; continue; }

    const partialIdx = normalized.findIndex((n) =>
      n.includes(field.key) || field.key.includes(n),
    );
    if (partialIdx >= 0 && !Object.values(map).includes(csvHeaders[partialIdx])) {
      map[field.key] = csvHeaders[partialIdx];
    }
  }
  return map;
}

export function CsvImportPage() {
  const [step, setStep] = useState<Step>("upload");
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [previewRows, setPreviewRows] = useState<Record<string, string>[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [defaults, setDefaults] = useState<Record<string, string>>({ status: "approved" });
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const previewMut = useMutation({
    mutationFn: async (csv: string) => {
      const res = await api.post("/v1/import/conversions/preview", { csv });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || "Preview failed"); }
      return res.json();
    },
    onSuccess: (data) => {
      setCsvHeaders(data.headers);
      setPreviewRows(data.preview);
      setTotalRows(data.totalRows);
      setColumnMap(autoMap(data.headers));
      setStep("map");
    },
  });

  const importMut = useMutation({
    mutationFn: async () => {
      const res = await api.post("/v1/import/conversions/import", { csv: csvText, columnMap, defaults });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || "Import failed"); }
      return res.json() as Promise<ImportResult>;
    },
    onSuccess: (data) => { setResult(data); setStep("results"); },
  });

  const handleFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      setCsvText(text);
      previewMut.mutate(text);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  function updateMapping(targetField: string, csvCol: string) {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (csvCol === "__none__") { delete next[targetField]; }
      else { next[targetField] = csvCol; }
      return next;
    });
  }

  function resetAll() {
    setStep("upload");
    setCsvText("");
    setFileName("");
    setCsvHeaders([]);
    setPreviewRows([]);
    setTotalRows(0);
    setColumnMap({});
    setDefaults({ status: "approved" });
    setResult(null);
  }

  const requiredMapped = TARGET_FIELDS
    .filter((f) => f.required)
    .every((f) => columnMap[f.key] || defaults[f.key]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Conversions</h1>
          <p className="text-sm text-gray-500 mt-1">Upload a CSV file to bulk-import conversion data</p>
        </div>
        {step !== "upload" && (
          <button onClick={resetAll} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X className="w-4 h-4" /> Start Over
          </button>
        )}
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 text-sm">
        {(["upload", "map", "results"] as const).map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <ArrowRight className="w-4 h-4 text-gray-300" />}
            <span className={cn(
              "px-3 py-1 rounded-full font-medium",
              step === s || (step === "importing" && s === "results")
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-400",
            )}>
              {i + 1}. {s === "upload" ? "Upload" : s === "map" ? "Map Columns" : "Results"}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Upload */}
      {step === "upload" && (
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-16 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
          />
          <Upload className="w-10 h-10 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700">Drop your CSV file here</p>
          <p className="text-sm text-gray-400 mt-2">or click to browse</p>
          {previewMut.isPending && (
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" />
              Parsing {fileName}...
            </div>
          )}
          {previewMut.isError && (
            <p className="mt-4 text-sm text-red-600">{previewMut.error.message}</p>
          )}

          <div className="mt-8 text-left mx-auto max-w-md">
            <p className="text-xs font-medium text-gray-500 mb-2">Required columns:</p>
            <p className="text-xs text-gray-400">transaction_id, offer_id, affiliate_id</p>
            <p className="text-xs font-medium text-gray-500 mt-3 mb-2">Optional columns:</p>
            <p className="text-xs text-gray-400">status, payout, revenue, sale_amount, country_code, event_name, sub1-sub5, source_id, created_at, currency_id, coupon_code, ...</p>
          </div>
        </div>
      )}

      {/* Step 2: Column Mapping */}
      {step === "map" && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900">{fileName}</p>
                <p className="text-xs text-gray-500">{totalRows} rows &middot; {csvHeaders.length} columns</p>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-3">Column Mapping</h3>
            <p className="text-xs text-gray-500 mb-4">Map your CSV columns to the conversion fields. Required fields are marked with *.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {TARGET_FIELDS.map((field) => (
                <div key={field.key} className="flex items-center gap-2">
                  <label className={cn("text-sm w-36 shrink-0", field.required ? "font-medium text-gray-900" : "text-gray-600")}>
                    {field.label}{field.required && <span className="text-red-500 ml-0.5">*</span>}
                  </label>
                  <div className="relative flex-1">
                    <select
                      value={columnMap[field.key] || "__none__"}
                      onChange={(e) => updateMapping(field.key, e.target.value)}
                      className={cn(
                        "w-full text-sm rounded-lg border px-3 py-1.5 appearance-none pr-8",
                        columnMap[field.key]
                          ? "border-gray-300 text-gray-900"
                          : field.required && !defaults[field.key]
                            ? "border-red-300 text-red-400"
                            : "border-gray-200 text-gray-400",
                      )}
                    >
                      <option value="__none__">-- Skip --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">Default Values (used when column is not mapped)</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500">Default Status</label>
                  <select
                    value={defaults.status || "approved"}
                    onChange={(e) => setDefaults((d) => ({ ...d, status: e.target.value }))}
                    className="w-full text-sm rounded-lg border border-gray-300 px-3 py-1.5"
                  >
                    <option value="approved">Approved</option>
                    <option value="pending">Pending</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Default Currency</label>
                  <input
                    value={defaults.currency_id || "USD"}
                    onChange={(e) => setDefaults((d) => ({ ...d, currency_id: e.target.value }))}
                    className="w-full text-sm rounded-lg border border-gray-300 px-3 py-1.5"
                    placeholder="USD"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Default Event Name</label>
                  <input
                    value={defaults.event_name || ""}
                    onChange={(e) => setDefaults((d) => ({ ...d, event_name: e.target.value }))}
                    className="w-full text-sm rounded-lg border border-gray-300 px-3 py-1.5"
                    placeholder="conversion"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Preview table */}
          {previewRows.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview (first {previewRows.length} rows)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {csvHeaders.map((h) => (
                        <th key={h} className="text-left py-2 px-3 font-medium text-gray-500 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        {csvHeaders.map((h) => (
                          <td key={h} className="py-1.5 px-3 text-gray-600 whitespace-nowrap truncate max-w-[200px]">
                            {row[h] || ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button onClick={() => setStep("upload")} className="text-sm text-gray-500 hover:text-gray-700">
              Back
            </button>
            <button
              onClick={() => { setStep("importing"); importMut.mutate(); }}
              disabled={!requiredMapped || importMut.isPending}
              className="px-6 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              Import {totalRows} Conversions
            </button>
          </div>
        </div>
      )}

      {/* Importing state */}
      {step === "importing" && (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700">Importing conversions...</p>
          <p className="text-sm text-gray-400 mt-2">Processing {totalRows} rows. This may take a moment.</p>
          {importMut.isError && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm text-left max-w-md mx-auto">
              <p className="font-medium">Import failed</p>
              <p className="mt-1">{importMut.error.message}</p>
              <button onClick={resetAll} className="mt-3 text-red-600 underline text-xs">Start Over</button>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Results */}
      {step === "results" && result && (
        <div className="space-y-6">
          <div className={cn(
            "rounded-xl border p-6",
            result.errors === 0 ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200",
          )}>
            <div className="flex items-center gap-3 mb-3">
              {result.errors === 0 ? (
                <Check className="w-6 h-6 text-emerald-600" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              )}
              <h3 className="text-lg font-semibold text-gray-900">Import Complete</h3>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-gray-900">{result.totalRows}</p>
                <p className="text-xs text-gray-500 mt-1">Total Rows</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{result.imported}</p>
                <p className="text-xs text-gray-500 mt-1">Imported</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className={cn("text-2xl font-bold", result.errors > 0 ? "text-red-600" : "text-gray-400")}>{result.errors}</p>
                <p className="text-xs text-gray-500 mt-1">Errors</p>
              </div>
            </div>
          </div>

          {result.errorDetails.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Error Details</h3>
              <div className="overflow-y-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Row</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-500">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errorDetails.map((err, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="py-2 px-3 text-gray-700 font-mono">{err.row}</td>
                        <td className="py-2 px-3 text-red-600">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button onClick={resetAll} className="px-6 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors">
              Import Another File
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
